import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react"
import Icon from "../../Icon"
import Skeleton from "../../Skeleton"
import api from "../../../utils/api"
import UtilityOverviewTiles from "./utilities/UtilityOverviewTiles"
import UtilityCompanyLists from "./utilities/UtilityCompanyLists"
import { PropertyWalletList, UtWalletList } from "./utilities/UtilityWalletLists"
import {
  COMPANY_CATEGORIES,
  matchesCompanySearch,
  matchesPropertyWalletSearch,
  matchesWalletSearch,
} from "./utilities/utilityAdminDisplay"

const IMPERSONATE_DESTINATION = "/app/ut-bills"

const TABS = [
  { key: "companies", label: "Companies", placeholder: "Search company or customer" },
  { key: "utWallets", label: "People's wallets", placeholder: "Search name, phone or company" },
  { key: "propertyWallets", label: "Landlord wallets", placeholder: "Search property or landlord" },
]

function LoadingTiles() {
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" aria-busy="true" aria-label="Loading overview">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="rounded-xl border border-gray-100 bg-white p-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-2 h-5 w-24" />
          <Skeleton className="mt-1.5 h-2.5 w-16" />
        </div>
      ))}
    </div>
  )
}

function AdminUtilitiesModuleSection({ onImpersonateUser }) {
  const [collapsed, setCollapsed] = useState(true)
  const [tab, setTab] = useState("companies")
  const [searchInput, setSearchInput] = useState("")
  const search = useDeferredValue(searchInput)
  const [companyCategory, setCompanyCategory] = useState(COMPANY_CATEGORIES[0].key)

  const [overview, setOverview] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const res = await api.get("/ut-bills/admin/overview")
      setOverview(res?.data || null)
    } catch (loadError) {
      console.error("Failed to load admin utilities overview", loadError)
      setError(loadError?.response?.data?.error || "Failed to load the Bills & Invoices overview.")
    } finally {
      setLoaded(true)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!collapsed && !loaded && !loading) loadOverview()
  }, [collapsed, loaded, loading, loadOverview])

  const companies = useMemo(
    () => (overview?.companies || []).filter((company) => matchesCompanySearch(company, search)),
    [overview, search]
  )
  const utWallets = useMemo(
    () => (overview?.utWallets || []).filter((wallet) => matchesWalletSearch(wallet, search)),
    [overview, search]
  )
  const propertyWallets = useMemo(
    () => (overview?.propertyWallets || []).filter((wallet) => matchesPropertyWalletSearch(wallet, search)),
    [overview, search]
  )

  const impersonate = onImpersonateUser
    ? (userId) => onImpersonateUser(userId, { destination: IMPERSONATE_DESTINATION })
    : undefined

  const summary = overview?.summary || {}
  const activeTab = TABS.find((item) => item.key === tab) || TABS[0]
  const tabCounts = {
    companies: (overview?.companies || []).length,
    utWallets: (overview?.utWallets || []).length,
    propertyWallets: (overview?.propertyWallets || []).length,
  }
  const openTab = (key) => {
    setTab(key)
    setSearchInput("")
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 shadow-lg shadow-emerald-100">
            <Icon name="droplet-half" size="1.25rem" className="text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-tight text-gray-900">Bills &amp; Invoices Management</h4>
            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Companies / Customers / Wallets</p>
          </div>
        </div>
        <Icon name={collapsed ? "chevron-right" : "chevron-down"} size="1rem" className="text-gray-300" />
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100">
          <div className="space-y-3 bg-gray-50/40 p-3 sm:p-4">
            {loading && !overview ? (
              <LoadingTiles />
            ) : overview ? (
              <UtilityOverviewTiles
                summary={summary}
                period={overview.period}
                onOpenUtWallets={() => openTab("utWallets")}
                onOpenPropertyWallets={() => openTab("propertyWallets")}
              />
            ) : null}

            {error && (
              <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
            )}

            <div className="grid grid-cols-3 gap-1 rounded-lg bg-gray-100 p-1" role="tablist" aria-label="Bills &amp; Invoices lists">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.key}
                  onClick={() => openTab(item.key)}
                  className={`min-w-0 rounded-md px-1.5 py-2 text-[11px] font-semibold leading-tight transition-colors sm:text-xs ${
                    tab === item.key ? "bg-white text-emerald-700 shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {item.label}
                  <span className={`ml-1 font-normal ${tab === item.key ? "text-emerald-600" : "text-gray-400"}`}>
                    {tabCounts[item.key]}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                  <Icon name="search" size="0.875rem" className="text-gray-400" />
                </div>
                <input
                  type="search"
                  placeholder={activeTab.placeholder}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <button
                type="button"
                onClick={loadOverview}
                disabled={loading}
                className="flex-shrink-0 rounded-lg border border-gray-300 bg-white p-2.5 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                title="Refresh"
                aria-label="Refresh overview"
              >
                <Icon name="arrow-clockwise" size="0.875rem" className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100">
            {loading && !overview ? (
              <p className="p-4 text-xs text-gray-500">Loading...</p>
            ) : tab === "companies" ? (
              <UtilityCompanyLists
                companies={companies}
                category={companyCategory}
                onCategoryChange={setCompanyCategory}
                unlinked={overview?.unlinked || null}
                summary={summary}
                search={search}
                onImpersonate={impersonate}
              />
            ) : tab === "utWallets" ? (
              <UtWalletList wallets={utWallets} searchActive={Boolean(search.trim())} onLogin={impersonate} />
            ) : (
              <PropertyWalletList wallets={propertyWallets} searchActive={Boolean(search.trim())} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUtilitiesModuleSection
